import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  forceRefreshUserData: () => Promise<User | null>;
  refreshToken: () => Promise<boolean>;
  changePassword: (email: string, currentPassword: string, newPassword: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // Changed to true initially
  const userRef = useRef<User | null>(null);
  
  // Keep ref in sync with state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const login = async (email: string, password: string) => {
    console.log('=== LOGIN ATTEMPT ===');
    console.log('Login attempt with email:', email);

    try {
      // First check if API is accessible
      try {
        const testResponse = await axios.get('/api/auth/test-login');
        console.log('API test endpoint accessible:', testResponse.data);
      } catch (testError) {
        console.error('API test endpoint failed:', testError);
        // Continue anyway, but log the error
      }

      console.log('Sending login request to /api/auth/login');
      const response = await axios.post('/api/auth/login', { email, password });
      console.log('Login response received:', {
        status: response.status,
        hasToken: !!response.data?.token,
        hasUser: !!response.data?.user,
        requirePasswordChange: !!response.data?.requirePasswordChange
      });

      // Check if password change is required
      if (response.data?.requirePasswordChange) {
        console.log('Password change required');
        // Store the temporary user data for password change flow
        sessionStorage.setItem('tempUser', JSON.stringify(response.data.user));
        throw new Error('FORCE_PASSWORD_CHANGE');
      }

      const { token, user: userData } = response.data;

      // Validate token before storing it
      if (!token || token === 'undefined') {
        console.error('Invalid token received from server:', token);
        throw new Error('Invalid authentication token received');
      }

      // Free token structure validation
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        console.error('Invalid token structure:', token.substring(0, 10) + '...');
        throw new Error('Invalid token format');
      }

      // Log token payload for debugging
      try {
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('Token payload:', {
          id: payload.id,
          email: payload.email,
          role: payload.role,
          exp: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'none'
        });
      } catch (err) {
        console.error('Error decoding token payload:', err);
      }

      // Store token
      localStorage.setItem('token', token);

      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Check if userData is valid
      if (!userData || !userData.id) {
        console.error('Invalid user data received:', userData);
        throw new Error('Invalid user data received');
      }

      // Check if image_url exists, if not, assign placeholder
      if (!userData.image_url) {
        userData.image_url = '/images/logo1.jpg';
      }

      console.log('Login successful, setting user state with data:', {
        id: userData.id,
        email: userData.email,
        role: userData.role
      });

      // Update user state
      setUser(userData);

      // Force a small delay to ensure state updates are processed
      await new Promise(resolve => setTimeout(resolve, 50));

      // Dispatch a custom event to notify components about auth state change
      console.log('Login successful, dispatching auth-state-changed event');
      window.dispatchEvent(new Event('auth-state-changed'));

      // Log success
      console.log('Login successful, user state updated');

      return userData;
    } catch (error: any) {
      console.error('Login error details:', {
        message: error.message,
        response: error.response ? {
          status: error.response.status,
          data: error.response.data
        } : 'No response',
        request: !!error.request
      });

      // Clean up any potentially problematic tokens
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];

      // Provide more specific error information
      if (error.response) {
        // Server responded with error
        const errorMessage = error.response.data?.message || 'Authentication failed';
        throw new Error(errorMessage);
      } else if (error.request) {
        // Request made but no response received
        throw new Error('No response from server. Please check your connection.');
      }

      throw error;
    }
  };

  const logout = useCallback(async () => {
    console.log('=== LOGOUT CALLED ===');

    try {
      const token = localStorage.getItem('token');

      // Only attempt server logout if we have a token
      if (token) {
        try {
          // Notify the server about logout to invalidate the token
          await axios.post('/api/auth/logout', {}, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          console.log('Server-side logout successful');
        } catch (error) {
          // If server logout fails, just log it but continue with client-side logout
          console.warn('Server-side logout failed, continuing with client-side logout:', error);
        }
      }
    } catch (error) {
      console.error('Error during logout process:', error);
    } finally {
      // Always perform client-side logout regardless of server response
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);

      // Dispatch a custom event to notify components about auth state change
      console.log('Logout completed, dispatching auth-state-changed event');
      window.dispatchEvent(new Event('auth-state-changed'));

      console.log('Client-side logout completed');
    }
  }, []);

  const checkAuth = useCallback(async () => {
    console.log('=== CHECK AUTH CALLED ===');
    const token = localStorage.getItem('token');
    console.log('Token exists:', !!token);

    // If no token, clear user state and return
    if (!token) {
      console.warn('No token found, user is not authenticated');
      if (userRef.current !== null) { // Only update and dispatch if state changes
        setUser(null);
        window.dispatchEvent(new Event('auth-state-changed'));
        console.log('AuthContext: User set to null by checkAuth, event dispatched.');
      }
      setLoading(false);
      return;
    }

    let userChanged = false;
    const initialUser = userRef.current; // Capture initial user state for comparison

    try {
      // Validate token structure
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        console.error('Invalid token structure, logging out');
        localStorage.removeItem('token');
        localStorage.removeItem('isAdminSession');
        delete axios.defaults.headers.common['Authorization'];
        if (userRef.current !== null) {
          setUser(null);
          userChanged = true;
        }
        return;
      }

      // Log token payload for debugging
      try {
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('Current token payload:', {
          id: payload.id,
          email: payload.email,
          role: payload.role,
          exp: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'none'
        });

        const now = Math.floor(Date.now() / 1000);
        if (payload.exp && payload.exp < now) {
          console.error('Token is expired, logging out');
          localStorage.removeItem('token');
          localStorage.removeItem('isAdminSession');
          delete axios.defaults.headers.common['Authorization'];
          if (userRef.current !== null) {
            setUser(null);
            userChanged = true;
          }
          return;
        }
      } catch (err) {
        console.error('Error decoding token:', err);
        localStorage.removeItem('token');
        localStorage.removeItem('isAdminSession');
        delete axios.defaults.headers.common['Authorization'];
        if (userRef.current !== null) {
          setUser(null);
          userChanged = true;
        }
        return;
      }

      setLoading(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      console.log('Fetching user data from /api/auth/user');
      const response = await axios.get('/api/auth/user', {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      });

      if (response.data && response.data.id) {
        console.log('User data received successfully:', {
          id: response.data.id,
          email: response.data.email,
          role: response.data.role,
          subscription_tier: response.data.subscription_tier,
          image_url: response.data.image_url ? 'present' : 'missing'
        });

        // Handle image URL processing - be conservative about timestamps
        let processedImageUrl = response.data.image_url;
        if (!processedImageUrl) {
          processedImageUrl = '/images/logo1.jpg';
        }
        // Don't add timestamp unless absolutely necessary - this was causing infinite loops
        
        // Create the user data object
        const newUserData = {
          ...response.data,
          image_url: processedImageUrl
        };

        // Improved comparison function that ignores timestamp parameters in image URLs
        const compareUserData = (user1: User | null, user2: User) => {
          if (!user1) return false;
          
          // Compare only essential fields to avoid issues with field order or missing fields
          const getComparableData = (user: User) => ({
            id: user.id,
            email: user.email,
            name: user.name || null,
            username: user.username || null,
            role: user.role,
            subscription_tier: user.subscription_tier || 'Basic',
            subscription_status: user.subscription_status || 'active',
            image_url: user.image_url ? user.image_url.split('?')[0] : '/images/logo1.jpg'
          });
          
          const comparable1 = getComparableData(user1);
          const comparable2 = getComparableData(user2);
          
          // Compare each field individually 
          const fieldsMatch = (Object.keys(comparable1) as Array<keyof typeof comparable1>).every(key => {
            return comparable1[key] === comparable2[key];
          });
          
          return fieldsMatch;
        };

        // Check if user data is actually different before setting (excluding timestamp differences)
        if (!compareUserData(userRef.current, newUserData)) {
          console.log('User data has changed, updating state');
          setUser(newUserData);
          userChanged = true;
        } else {
          console.log('User data unchanged, skipping state update');
        }

      } else {
        console.warn('No valid user data in response');
        if (userRef.current !== null) {
          setUser(null);
          userChanged = true;
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('isAdminSession');
        delete axios.defaults.headers.common['Authorization'];
        if (userRef.current !== null) {
          setUser(null);
          userChanged = true;
        }
      }
      // If it's not a 401, we might not want to clear the user immediately,
      // maybe it's a temporary network issue.
      // However, if an error occurs, it's safer to assume auth state might be invalid.
      // For now, only changing user on explicit conditions or 401.
    } finally {
      setLoading(false);
      // Dispatch event only if user state actually changed
      if (userChanged || (initialUser === null && userRef.current !== null) || (initialUser !== null && userRef.current === null) ) {
        // A more robust check: compare stringified initialUser and current user from context after setUser
        // For simplicity here, userChanged flag is the primary driver.
        // The condition `(initialUser === null && user !== null) || (initialUser !== null && user === null)`
        // is redundant if userChanged is set correctly in all paths.
        // Re-evaluating the user state from context directly is safer for comparison if setUser is async.
        // However, `userChanged` should capture most cases.
        // Let's assume `userChanged` is set correctly in all relevant paths leading to a state change.
        if (userChanged) {
             window.dispatchEvent(new Event('auth-state-changed'));
             console.log('AuthContext: checkAuth finished, event dispatched due to user state change.');
        }
      }
    }
  }, []); // Removed user dependency to prevent infinite loop

  const forceRefreshUserData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/auth/user?_bust=' + Date.now());
      if (response.data && response.data.id) {
        setUser(response.data);
        userRef.current = response.data;
        return response.data;
      }
      return null;
    } catch (error) {
      setUser(null);
      userRef.current = null;
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a token refresh function
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token to refresh');
        return false;
      }

      // Attempt to refresh the token
      const response = await axios.post('/api/auth/refresh-token', {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data && response.data.token) {
        // Update token in localStorage
        localStorage.setItem('token', response.data.token);

        // Update user data
        if (response.data.user) {
          setUser(response.data.user);
        } else {
          // If user data not included, fetch it directly
          // We'll manually fetch user data instead of calling checkAuth to avoid circular dependency
          try {
            const userResponse = await axios.get('/api/auth/user', {
              headers: {
                Authorization: `Bearer ${response.data.token}`,
                'Cache-Control': 'no-cache, no-store, must-revalidate'
              }
            });

            if (userResponse.data && userResponse.data.id) {
              setUser(userResponse.data);
            }
          } catch (userError) {
            console.error('Error fetching user data after token refresh:', userError);
          }
        }

        console.log('Token refreshed successfully');
        return true;
      }

      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, perform direct logout actions
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      return false;
    }
  }, []);

  // Single useEffect for auth initialization and interceptor setup
  useEffect(() => {
    let interceptor: number;

    const initializeAuth = async () => {
      try {
        // Only check auth once during initialization
        await checkAuth();      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);

        // Set up axios interceptor after auth is initialized
        interceptor = axios.interceptors.request.use(
          (config) => {
            const token = localStorage.getItem('token');

            // Skip token validation for public endpoints
            if (config.url?.includes('/api/auth/login') || config.url?.includes('/api/auth/register')) {
              return config;
            }

            // Check if token is null, undefined, or the literal string "undefined"
            if (!token || token === "undefined") {
              // Skip warning for public endpoints (no auth required)
              const isPublicUrl = config.url?.includes('/api/support-tickets') ||
                config.url?.includes('/api/listings/store/') ||
                config.url?.includes('/api/listings/by-store/');
              if (config.url?.includes('/api/') && !isPublicUrl) {
                console.warn('⚠️ No valid token found for API request:', config.url);
              }
              return config;
            }

            // Validate token format
            const tokenParts = token.split('.');
            if (tokenParts.length !== 3) {
              console.error('❌ Invalid token format detected');
              localStorage.removeItem('token');
              return config;
            }

            try {
              const payload = JSON.parse(atob(tokenParts[1]));
              const now = Math.floor(Date.now() / 1000);

              if (payload.exp && payload.exp < now) {
                console.warn('⚠️ Token has expired, removing from storage');
                localStorage.removeItem('token');
                return config;
              }

              config.headers.Authorization = `Bearer ${token}`;
            } catch (error) {
              console.error('❌ Error validating token:', error);
              localStorage.removeItem('token');
            }

            return config;
          },
          (error) => {
            return Promise.reject(error);
          }
        );
      }
    };

    initializeAuth();

    // Clean up interceptor on unmount
    return () => {
      if (interceptor !== undefined) {
        axios.interceptors.request.eject(interceptor);
      }
    };
  }, [checkAuth]);

  const changePassword = async (email: string, currentPassword: string, newPassword: string) => {
    setLoading(true);

    try {
      const response = await axios.post('/api/auth/change-forced-password', {
        email,
        currentPassword,
        newPassword
      });

      if (response.data?.success) {
        console.log('Password changed successfully');
        // Clear temporary user data
        sessionStorage.removeItem('tempUser');
        // User will need to log in again with new password
        return;
      } else {
        throw new Error(response.data?.message || 'Failed to change password');
      }
    } catch (error: any) {
      console.error('Password change error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      checkAuth,
      forceRefreshUserData,
      refreshToken,
      changePassword
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Note: The useAuth hook has been moved to src/hooks/useAuth.tsx
// Please import it from '../hooks/useAuth' in all components
